import type { ElementType, HTMLProps, ReactNode } from 'react';

import {
	type LayoutMode,
	type LayoutSpacing,
	type ResponsiveValue,
	toResponsiveValue,
	toSpacingValue,
	useContainerContext,
} from '@playbook/modules/layout/container/container-context';
import { type ClassName, cn, cv } from '@playbook/utils';

import css from './styles.module.css';

const DefaultLayoutGutter: LayoutSpacing = 10;

const rowLayoutClassNames: Record<LayoutMode, ClassName> = {
	container: css.rowContainer,
	viewport: css.rowViewport,
};

type VerticalAlign = 'start' | 'center' | 'end' | 'stretch';

type HorizontalAlign = 'normal' | 'start' | 'center' | 'end';

type GridJustify =
	| 'normal'
	| 'start'
	| 'space-between'
	| 'end'
	| 'center'
	| 'stretch';

interface RowProps extends Omit<HTMLProps<HTMLDivElement>, 'as'> {
	// Element to render instead of the default <section> (e.g. "h1", "ul").
	as?: ElementType;

	spacing?: ResponsiveValue<LayoutSpacing>;
	inline?: boolean;

	align?: ResponsiveValue<VerticalAlign>;
	justify?: ResponsiveValue<HorizontalAlign>;
	justifyContent?: ResponsiveValue<GridJustify>;

	autoColumns?: ResponsiveValue<string>;
	columns?: ResponsiveValue<string>;

	collapse?: 'reverse' | 'initial';

	className?: string;

	children?: ReactNode | ReactNode[];
}

export function Row({
	as: Component = 'section',

	spacing = DefaultLayoutGutter,
	inline,

	align = 'start',
	justify = 'normal',
	justifyContent = 'normal',

	autoColumns = '1fr',
	columns = '',

	collapse = 'initial',

	className,

	style,

	children,

	...props
}: RowProps): ReactNode {
	const layout = useContainerContext();
	const inlineClassName = inline ? css.inline : null;

	const layoutClassName = layout ? rowLayoutClassNames[layout.mode] : null;

	const [defaultSpacing, mdSpacing, lgSpacing] = toResponsiveValue(spacing);
	const [defaultAlign, mdAlign, lgAlign] = toResponsiveValue(align);
	const [defaultJustify, mdJustify, lgJustify] = toResponsiveValue(justify);

	const [defaultJustifyContent, mdJustifyContent, lgJustifyContent] =
		toResponsiveValue(justifyContent);

	const [defaultAutoColumns, mdAutoColumns, lgAutoColumns] =
		toResponsiveValue(autoColumns);

	const [defaultColumns, mdColumns, lgColumns] = toResponsiveValue(columns);

	let collapseClassName: string | null | undefined = null;

	if (collapse === 'reverse') {
		if (mdColumns === defaultColumns) {
			collapseClassName = css.rowCollapseReverseUntilLg;
		} else {
			collapseClassName = css.rowCollapseReverse;
		}
	}

	return (
		<Component
			{...props}
			className={cn(
				css.row,
				layoutClassName,
				collapseClassName,
				inlineClassName,
				className
			)}
			style={{
				...style,
				...cv({
					spacing: toSpacingValue(defaultSpacing),
					spacingResponsive: toSpacingValue(mdSpacing),
					spacingResponsiveLg: toSpacingValue(lgSpacing),
					align: defaultAlign,
					alignResponsive: mdAlign,
					alignResponsiveLg: lgAlign,
					justify: defaultJustify,
					justifyResponsive: mdJustify,
					justifyResponsiveLg: lgJustify,
					justifyContent: defaultJustifyContent,
					justifyContentResponsive: mdJustifyContent,
					justifyContentResponsiveLg: lgJustifyContent,
					autoColumns: defaultAutoColumns,
					autoColumnsResponsive: mdAutoColumns,
					autoColumnsResponsiveLg: lgAutoColumns,
					columns: defaultColumns,
					columnsResponsive: mdColumns,
					columnsResponsiveLg: lgColumns,
				}),
			}}>
			{children}
		</Component>
	);
}
